import { Location } from '../../types';
import { MapRefs } from './types';
import { createCustomIcon } from './constants';
import toast from 'react-hot-toast';

export const clearMarkers = (refs: MapRefs) => {
  if (refs.mapInstanceRef.current && refs.markersRef.current.length > 0) {
    refs.markersRef.current.forEach(marker => {
      try {
        refs.mapInstanceRef.current?.geoObjects.remove(marker);
      } catch (error) {
        // Игнорируем ошибки удаления
      }
    });
    refs.markersRef.current = [];
  }
};

export const updateUserLocationMarker = (
  coords: [number, number],
  refs: MapRefs
) => {
  if (!refs.mapInstanceRef.current) return;

  try {
    if (refs.userPlacemarkRef.current) {
      refs.userPlacemarkRef.current.geometry?.setCoordinates(coords);
    } else {
      refs.userPlacemarkRef.current = new window.ymaps.Placemark(coords, {
        hintContent: 'Ваша месцазнаходжанне'
      }, {
        preset: 'islands#geolocationIcon'
      });
      refs.mapInstanceRef.current.geoObjects.add(refs.userPlacemarkRef.current);
    }
  } catch (error) {
    console.error('Error updating user location marker:', error);
  }
};

export const updateMarkerPosition = (
  coords: [number, number],
  refs: MapRefs,
  onClick?: (lat: number, lng: number) => void
) => {
  if (!refs.mapInstanceRef.current) return;

  try {
    if (refs.placemarkRef.current) {
      refs.placemarkRef.current.geometry?.setCoordinates(coords);
    } else {
      refs.placemarkRef.current = new window.ymaps.Placemark(coords, {
        hintContent: 'Абраная лакацыя'
      }, {
        preset: 'islands#redDotIcon',
        draggable: Boolean(onClick)
      });
      refs.mapInstanceRef.current.geoObjects.add(refs.placemarkRef.current);

      if (onClick) {
        refs.placemarkRef.current.events.add('dragend', () => {
          const newCoords = refs.placemarkRef.current?.geometry?.getCoordinates();
          if (newCoords) {
            onClick(newCoords[0], newCoords[1]);
          }
        });
      }
    }
  } catch (error) {
    console.error('Error updating marker position:', error);
  }
};

// Дебаунс функция для оптимизации
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Кэш для balloon контента
const balloonCache = new Map<string, string>();

const createBalloonContent = (location: Location): string => {
  const cacheKey = location.id;
  
  if (balloonCache.has(cacheKey)) {
    return balloonCache.get(cacheKey)!;
  }

  const content = `
    <div style="
      width: 280px;
      background: white;
      border-radius: 12px;
      padding: 16px;
      font-family: 'Commissioner', system-ui, sans-serif;
    ">
      <h3 style="
        margin: 0 0 8px 0;
        font-size: 16px;
        font-weight: 700;
        color: #1f2937;
        line-height: 1.3;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      ">${location.name}</h3>
      
      <p style="
        margin: 0 0 12px 0;
        font-size: 14px;
        color: #6b7280;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      ">${location.shortDescription}</p>
      
      <button 
        onclick="window.handleLocationClick && window.handleLocationClick('${location.id}')"
        style="
          width: 100%;
          background: #4C9A2A;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
        "
      >
        Падрабязней
      </button>
    </div>
  `;

  balloonCache.set(cacheKey, content);
  return content;
};

export const addLocationMarkers = (
  locations: Location[],
  refs: MapRefs,
  showUserLocation: boolean,
  userLocation: [number, number] | null,
  onLocationClick?: (location: Location) => void,
  showHoverCard?: (location: Location, marker: ymaps.Placemark) => void,
  hideHoverCard?: () => void,
  showMobileBottomSheet?: (location: Location) => void
) => {
  if (!refs.mapInstanceRef.current) return;

  clearMarkers(refs);

  if (showUserLocation && userLocation) {
    updateUserLocationMarker(userLocation, refs);
  }

  // Глобальный обработчик клика
  (window as any).handleLocationClick = (locationId: string) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location && onLocationClick) {
      onLocationClick(location);
    }
  };

  // Определяем мобильное устройство
  const isMobile = window.innerWidth < 768;
  
  // Дебаунсированные обработчики для hover только на десктопе
  const debouncedShowHover = !isMobile && showHoverCard ? debounce(showHoverCard, 150) : null;
  const debouncedHideHover = !isMobile && hideHoverCard ? debounce(hideHoverCard, 100) : null;

  for (const location of locations) {
    try {
      // Используем кастомные SVG иконки
      const marker = new window.ymaps.Placemark(
        [location.coordinates.lat, location.coordinates.lng],
        {
          hintContent: location.name
        },
        {
          iconLayout: 'default#image',
          iconImageHref: createCustomIcon(location.category),
          iconImageSize: [32, 32],
          iconImageOffset: [-16, -16],
          // Мобильные оптимизации
          ...(isMobile && {
            zIndex: 100,
            zIndexHover: 200,
            zIndexActive: 300
          })
        }
      );

      // Hover события только для десктопа с дебаунсом
      if (!isMobile && debouncedShowHover && debouncedHideHover) {
        marker.events.add('mouseenter', () => debouncedShowHover(location, marker));
        marker.events.add('mouseleave', () => debouncedHideHover());
      }

      // Клик события с оптимизацией для мобильных
      marker.events.add('click', (e) => {
        // Предотвращаем всплытие события на мобильных для лучшей производительности
        if (isMobile) {
          e.preventDefault();
          e.stopPropagation();
        }
        
        if (isMobile && showMobileBottomSheet) {
          // На мобильных показываем bottom sheet
          showMobileBottomSheet(location);
        } else {
          // На десктопе показываем balloon
          if (refs.mapInstanceRef.current) {
            const balloonContent = createBalloonContent(location);
            marker.properties.set('balloonContent', balloonContent);
            marker.balloon.open();
          }
        }
      });

      refs.mapInstanceRef.current?.geoObjects.add(marker);
      refs.markersRef.current.push(marker);
    } catch (error) {
      console.error('Error adding location marker:', error);
    }
  }
};